/**
 * The catalog's state: what is in it, which tab is showing, and every mutation.
 *
 * Unlike Saved Views (localStorage, per browser) and dashboards (localStorage,
 * for now), a catalog chart lives in Postgres and belongs to somebody. That
 * changes three things this module has to get right:
 *
 * * **One fetch, three tabs.** `scope=all` returns what the three tabs show, and
 *   each row carries `is_owner`, `shared_directly` and `is_public`, so "My
 *   charts", "Shared with me" and "Public" are cut from one response rather than
 *   three requests that can disagree about the counts. They overlap on purpose:
 *   your own public chart is in "My charts" and in "Public".
 * * **Search is local.** The API can filter, but the list is small and typing
 *   should not wait for a round trip. `chartMatches` reaches further than the
 *   server's title/description search - a column name is often all you recall.
 * * **Failure is a state, not an exception.** With no database the section says
 *   so; with a broken one it says that instead. Neither may take Explore down,
 *   so nothing here throws into a caller that is drawing a chart.
 *
 * Opening a chart routes through `restoreFromUrl`, exactly as a saved view and
 * a dashboard tile do, so there is one restore path in the app and a chart is
 * just another way to reach it.
 */

import {
  changeChartGrant,
  createChart as apiCreateChart,
  deleteChart as apiDeleteChart,
  getChart,
  grantChart as apiGrantChart,
  listChartGrants,
  listCharts,
  revokeChartGrant,
  searchPrincipals,
  setChartPublic,
  updateChart as apiUpdateChart,
} from '../../api/endpoints.ts';
import type { CatalogChart, ChartGrant, PrincipalSearch } from '../../api/types.ts';
import { Atom, activeSection, activeTab, catalogAvailable } from '../../state/store.ts';
import { buildQueryString, restoreFromUrl, syncToUrl } from '../../state/url-sync.ts';
import { withCurrentContext } from '../../state/presets.ts';
import { showToast } from '../../ui/toast.ts';
import { shipLabel } from '../../utils/ships.ts';
import { doPlot } from '../plot/plot-actions.ts';
import { chartMatches, chartPinsShip, isTemplate } from './chart-summary.ts';
import { forgetThumbnails } from './thumbnail.ts';
import { userMessage } from '../../api/errors.ts';

export type CatalogScope = 'mine' | 'shared' | 'public';

/** Everything the viewer may see, owned and shared alike. */
export const catalogCharts = new Atom<CatalogChart[]>([]);
/** The id of the chart this tab saved last. The plot tour waits for it. */
export const lastSavedChart = new Atom<string | null>(null);
export const catalogLoading = new Atom<boolean>(false);
/** Null while fine; a sentence to show the user when not. */
export const catalogError = new Atom<string | null>(null);
export const catalogScope = new Atom<CatalogScope>('mine');
export const catalogQuery = new Atom<string>('');
/** True once a load has completed, so an empty list can be told from "not yet". */
export const catalogLoaded = new Atom<boolean>(false);

/* ── sorting ─────────────────────────────────────────────── */

export type CatalogSort = 'modified' | 'created' | 'name' | 'type' | 'owner';

export const CATALOG_SORT_KEY = 'neptune_catalog_sort_v1';

interface SortOption {
  value: CatalogSort;
  label: string;
  /** Owner means nothing on "My charts", where it is always you. */
  othersOnly?: boolean;
}

/**
 * Each order runs in the one direction people mean by it - newest first for
 * dates, A to Z for words - so there is no separate direction toggle to find.
 */
export const CATALOG_SORTS: readonly SortOption[] = [
  { value: 'modified', label: 'Last modified' },
  { value: 'created', label: 'Recently created' },
  { value: 'name', label: 'Name A-Z' },
  { value: 'type', label: 'Plot type' },
  { value: 'owner', label: 'Owner', othersOnly: true },
];

function readSort(): CatalogSort {
  try {
    const saved = localStorage.getItem(CATALOG_SORT_KEY);
    return CATALOG_SORTS.some((o) => o.value === saved) ? (saved as CatalogSort) : 'modified';
  } catch {
    return 'modified';
  }
}

/** The chosen order. Remembered per browser: it is a way of looking, not data. */
export const catalogSort = new Atom<CatalogSort>(readSort());

export function setCatalogSort(sort: CatalogSort): void {
  catalogSort.set(sort);
  try {
    localStorage.setItem(CATALOG_SORT_KEY, sort);
  } catch {
    // Private windows and blocked storage: the order still applies, for now.
  }
}

/** The orders a tab offers. */
export function sortsFor(scope: CatalogScope): SortOption[] {
  return CATALOG_SORTS.filter((o) => scope !== 'mine' || !o.othersOnly);
}

/**
 * The order a tab actually uses. "Owner" chosen on Shared or Public falls back
 * to last modified on My charts, without forgetting the choice for going back.
 */
export function effectiveSort(scope: CatalogScope): CatalogSort {
  const sort = catalogSort.value;
  return sortsFor(scope).some((o) => o.value === sort) ? sort : 'modified';
}

const byText = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
const newestFirst = (a: string, b: string): number => Date.parse(b) - Date.parse(a);

/** Ties on name, type or owner fall back to newest first, the default order. */
const COMPARE: Record<CatalogSort, (a: CatalogChart, b: CatalogChart) => number> = {
  modified: (a, b) => newestFirst(a.updated_at, b.updated_at),
  created: (a, b) => newestFirst(a.created_at, b.created_at),
  name: (a, b) => byText(a.title, b.title) || newestFirst(a.updated_at, b.updated_at),
  type: (a, b) => byText(a.plot_type, b.plot_type) || newestFirst(a.updated_at, b.updated_at),
  owner: (a, b) =>
    byText(a.owner.display_name, b.owner.display_name) || newestFirst(a.updated_at, b.updated_at),
};

/* ── filtering ───────────────────────────────────────────── */

/**
 * The filter row: each facet holds the values ticked, and an empty facet means
 * "any". Values are what the chart carries - an owner id, a plot type, a ship
 * code, a tag - so a filter never depends on how a label is spelled.
 */
export interface CatalogFilters {
  people: string[];
  types: string[];
  kinds: string[];
  ships: string[];
  tags: string[];
}

export type FilterFacet = keyof CatalogFilters;

export const NO_FILTERS: CatalogFilters = { people: [], types: [], kinds: [], ships: [], tags: [] };

/** Session-only, and cleared on changing tab - see `setCatalogScope`. */
export const catalogFilters = new Atom<CatalogFilters>(NO_FILTERS);

export interface FilterOption {
  value: string;
  label: string;
  count: number;
}

/** "Template" or "Pinned" - the one fact that changes what opening a chart shows. */
function kindOf(chart: CatalogChart): string {
  return isTemplate(chart) ? 'template' : 'pinned';
}

/** Every ship a pinned chart names; a template names none. */
function shipsOf(chart: CatalogChart): string[] {
  if (!chartPinsShip(chart)) return [];
  const context = chart.context ?? {};
  const extra = (context.ships ?? '').split(',').filter(Boolean);
  return [...new Set([context.ship ?? '', ...extra].filter(Boolean))];
}

/** The values one chart holds for one facet. */
function valuesOf(chart: CatalogChart, facet: FilterFacet): string[] {
  switch (facet) {
    case 'people':
      return [chart.owner.id];
    case 'types':
      return [chart.plot_type];
    case 'kinds':
      return [kindOf(chart)];
    case 'ships':
      return shipsOf(chart);
    case 'tags':
      return chart.tags;
  }
}

/** Facets that mean something on a tab. People is always you on "My charts". */
export function facetsFor(scope: CatalogScope): FilterFacet[] {
  const all: FilterFacet[] = ['people', 'types', 'kinds', 'ships', 'tags'];
  return scope === 'mine' ? all.filter((f) => f !== 'people') : all;
}

function labelFor(facet: FilterFacet, value: string, chart: CatalogChart): string {
  switch (facet) {
    case 'people':
      return chart.is_owner ? 'You' : chart.owner.display_name;
    case 'kinds':
      return value === 'template' ? 'Template' : 'Pinned';
    case 'ships':
      return shipLabel(value);
    case 'types':
      return value.charAt(0).toUpperCase() + value.slice(1);
    default:
      return value;
  }
}

/**
 * What a facet can be narrowed to on this tab, with how many charts each has.
 *
 * Counted over the tab's charts before the other filters, so a count says what
 * the tab holds rather than shrinking as you tick - and only values that some
 * chart actually has are offered, so no choice leads to an empty grid.
 */
export function filterOptions(scope: CatalogScope, facet: FilterFacet): FilterOption[] {
  const found = new Map<string, FilterOption>();
  for (const chart of scopedCharts(scope)) {
    for (const value of valuesOf(chart, facet)) {
      const option = found.get(value);
      if (option) option.count += 1;
      else found.set(value, { value, label: labelFor(facet, value, chart), count: 1 });
    }
  }
  return [...found.values()].sort(
    (a, b) => b.count - a.count || a.label.localeCompare(b.label),
  );
}

function passesFilters(chart: CatalogChart, filters: CatalogFilters, scope: CatalogScope): boolean {
  return facetsFor(scope).every((facet) => {
    const wanted = filters[facet];
    return !wanted.length || valuesOf(chart, facet).some((v) => wanted.includes(v));
  });
}

export function setFilter(facet: FilterFacet, values: string[]): void {
  catalogFilters.set({ ...catalogFilters.value, [facet]: values });
}

export function clearFilters(): void {
  catalogFilters.set(NO_FILTERS);
}

export function hasFilters(scope: CatalogScope = catalogScope.value): boolean {
  return facetsFor(scope).some((f) => catalogFilters.value[f].length > 0);
}

/**
 * Change tab. Filters are cleared because their values belong to the tab they
 * were picked on - a person ticked on Public may own nothing in Shared, and a
 * grid emptied by an invisible filter reads as a bug. The sort is kept: it is a
 * way of looking, not a selection.
 */
export function setCatalogScope(scope: CatalogScope): void {
  if (scope === catalogScope.value) return;
  catalogScope.set(scope);
  clearFilters();
}

/** The chart the share dialog is open for, and its grants. */
export const sharingChartId = new Atom<string | null>(null);
export const chartGrants = new Atom<ChartGrant[]>([]);
export const grantsLoading = new Atom<boolean>(false);

function message(error: unknown): string {
  return userMessage(error);
}

/* ── reading ─────────────────────────────────────────────── */

/** Whether one chart belongs on one tab. The tabs overlap; see the header. */
export function inScope(chart: CatalogChart, scope: CatalogScope): boolean {
  if (scope === 'mine') return chart.is_owner;
  if (scope === 'shared') return !chart.is_owner && chart.shared_directly;
  return chart.is_public;
}

export function scopedCharts(scope: CatalogScope): CatalogChart[] {
  return catalogCharts.value.filter((c) => inScope(c, scope));
}

/** What the active tab shows, after the search box, the filters and the sort. */
export function visibleCharts(): CatalogChart[] {
  const scope = catalogScope.value;
  const filters = catalogFilters.value;
  return scopedCharts(scope)
    .filter((c) => chartMatches(c, catalogQuery.value) && passesFilters(c, filters, scope))
    .sort(COMPARE[effectiveSort(scope)]);
}

export function chartById(id: string): CatalogChart | undefined {
  return catalogCharts.value.find((c) => c.id === id);
}

/**
 * Load the catalog. Safe to call on every visit to the section - it is one
 * small query, and a list that silently lags behind what someone just shared is
 * worse than a request.
 */
export async function loadCatalog(): Promise<void> {
  // Availability arrives with `/api/info`, which is still in flight while the
  // section first mounts. Returning "loaded and empty" here would be a lie that
  // sticks: the answer arrives a moment later and nothing would ask again. So
  // leave `catalogLoaded` false and let the caller retry when it knows.
  if (!catalogAvailable.value || catalogLoading.value) return;
  catalogLoading.set(true);
  catalogError.set(null);
  try {
    catalogCharts.set(await listCharts('all'));
    catalogLoaded.set(true);
  } catch (error) {
    catalogError.set(message(error));
  } finally {
    catalogLoading.set(false);
  }
}

/* ── opening ─────────────────────────────────────────────── */

/**
 * Open a chart in Explore and draw it.
 *
 * The saved ship and time dimensions replace only those parts of the viewer's
 * current context. The merged query goes through the app's one restore path.
 */
export async function openChart(chart: CatalogChart): Promise<void> {
  activeSection.set('explore');
  document.body.dataset.section = 'explore';
  activeTab.set('plot');
  document.body.dataset.activeTab = 'plot';
  await restoreFromUrl(`?${chartOpenQuery(chart)}`);
  void doPlot();
}

/** A saved chart's pinned dimensions over the viewer's current dimensions. */
export function chartOpenQuery(chart: CatalogChart): string {
  if (chart.pin_ship === undefined && chart.pin_time === undefined && chart.bind_context) {
    return chart.query;
  }
  const query = new URLSearchParams(withCurrentContext(chart.query));
  for (const [key, value] of Object.entries(chart.context ?? {})) query.set(key, value);
  return query.toString();
}

/* ── writing ─────────────────────────────────────────────── */

/** Save whatever Explore is showing as a named chart. */
export async function saveCurrentAsChart(details: {
  title: string;
  description?: string;
  tags?: string[];
  pinShip?: boolean;
  pinTime?: boolean;
  bindContext?: boolean;
}): Promise<CatalogChart | null> {
  syncToUrl();
  try {
    const chart = await apiCreateChart({
      title: details.title,
      query: buildQueryString(),
      description: details.description || null,
      bind_context: details.bindContext ?? !!(details.pinShip || details.pinTime),
      ...(details.bindContext === undefined ? { pin_ship: !!details.pinShip, pin_time: !!details.pinTime } : {}),
      tags: details.tags ?? [],
    });
    catalogCharts.update((charts) => [chart, ...charts]);
    lastSavedChart.set(chart.id);
    showToast('success', `Saved "${chart.title}" to your catalog`);
    return chart;
  } catch (error) {
    showToast('error', `Could not save the chart: ${message(error)}`);
    return null;
  }
}

/** Patch a chart's details. `version` makes a concurrent edit a 409, not a race. */
export async function updateChartDetails(
  id: string,
  patch: { title?: string; description?: string | null; tags?: string[]; bind_context?: boolean },
): Promise<boolean> {
  const current = chartById(id);
  try {
    const updated = await apiUpdateChart(
      id,
      current ? { ...patch, version: current.version } : patch,
    );
    catalogCharts.update((charts) => charts.map((c) => (c.id === id ? updated : c)));
    return true;
  } catch (error) {
    showToast('error', `Could not update the chart: ${message(error)}`);
    return false;
  }
}

/** Save the editor's full recipe over the version it opened. */
export async function updateChartRecipe(
  chart: CatalogChart,
  changes: { query: string; title: string; pinShip?: boolean; pinTime?: boolean; bindContext?: boolean },
): Promise<CatalogChart | null> {
  try {
    // The editor opens over the Catalog section. A stored chart is an Explore
    // recipe, not a route back to the Catalog (or to an open dashboard).
    const recipe = new URLSearchParams(changes.query);
    recipe.delete('section');
    recipe.delete('db');
    recipe.delete('atab');
    const updated = await apiUpdateChart(
      chart.id,
      {
        query: recipe.toString(),
        title: changes.title,
        bind_context: changes.bindContext ?? !!(changes.pinShip || changes.pinTime),
        ...(changes.bindContext === undefined ? { pin_ship: !!changes.pinShip, pin_time: !!changes.pinTime } : {}),
        version: chart.version,
      },
    );
    catalogCharts.update((charts) => charts.map((c) => (c.id === chart.id ? updated : c)));
    showToast('success', `Updated "${updated.title}"`);
    return updated;
  } catch (error) {
    showToast('error', `Could not update the chart: ${message(error)}`);
    return null;
  }
}

export async function removeChart(id: string): Promise<boolean> {
  const chart = chartById(id);
  try {
    await apiDeleteChart(id);
    catalogCharts.update((charts) => charts.filter((c) => c.id !== id));
    forgetThumbnails(id);
    showToast('success', `Deleted "${chart?.title ?? 'chart'}"`);
    return true;
  } catch (error) {
    showToast('error', `Could not delete the chart: ${message(error)}`);
    return false;
  }
}

/**
 * Copy somebody else's chart into your own catalog.
 *
 * This is how a viewer builds on shared work without needing edit rights on it,
 * and it is why "Shared with me" does not need an edit affordance to be useful.
 */
export async function duplicateChart(chart: CatalogChart): Promise<CatalogChart | null> {
  try {
    const copy = await apiCreateChart({
      title: `${chart.title} (copy)`,
      query: chart.query,
      description: chart.description,
      bind_context: chart.bind_context,
      ...(chart.pin_ship !== undefined ? { pin_ship: chart.pin_ship } : {}),
      ...(chart.pin_time !== undefined ? { pin_time: chart.pin_time } : {}),
      tags: chart.tags,
    });
    catalogCharts.update((charts) => [copy, ...charts]);
    setCatalogScope('mine');
    showToast('success', `Copied to your catalog as "${copy.title}"`);
    return copy;
  } catch (error) {
    showToast('error', `Could not copy the chart: ${message(error)}`);
    return null;
  }
}

/**
 * Re-read one chart into the list.
 *
 * Used after a preview is captured: the row is otherwise identical, but
 * `thumbnail_version` has appeared, and that is what tells the card there is a
 * picture to ask for.
 */
export async function refreshChart(id: string): Promise<void> {
  try {
    const fresh = await getChart(id);
    catalogCharts.update((charts) =>
      charts.some((c) => c.id === id)
        ? charts.map((c) => (c.id === id ? fresh : c))
        : [fresh, ...charts],
    );
  } catch {
    // The list is still correct in every way that matters; only the picture is
    // missing, and the next load will find it.
  }
}

/* ── sharing ─────────────────────────────────────────────── */

export async function openShareDialog(id: string): Promise<void> {
  sharingChartId.set(id);
  chartGrants.set([]);
  grantsLoading.set(true);
  try {
    chartGrants.set(await listChartGrants(id));
  } catch (error) {
    showToast('error', `Could not read who this is shared with: ${message(error)}`);
  } finally {
    grantsLoading.set(false);
  }
}

export function closeShareDialog(): void {
  sharingChartId.set(null);
  chartGrants.set([]);
}

export async function shareChart(
  id: string,
  principal: { user_id?: string; group_id?: string; everyone?: boolean },
  role: 'viewer' | 'editor',
  expiresAt?: string | null,
): Promise<boolean> {
  try {
    await apiGrantChart(id, { ...principal, role, expires_at: expiresAt ?? null });
    chartGrants.set(await listChartGrants(id));
    return true;
  } catch (error) {
    showToast('error', `Could not share the chart: ${message(error)}`);
    return false;
  }
}

export async function revokeShare(id: string, grantId: string): Promise<boolean> {
  try {
    await revokeChartGrant(id, grantId);
    chartGrants.set(await listChartGrants(id));
    return true;
  } catch (error) {
    showToast('error', `Could not withdraw access: ${message(error)}`);
    return false;
  }
}

/** Make an existing grant viewer or editor. */
export async function changeShareRole(
  id: string,
  grantId: string,
  role: 'viewer' | 'editor',
): Promise<boolean> {
  try {
    await changeChartGrant(id, grantId, role);
    chartGrants.set(await listChartGrants(id));
    return true;
  } catch (error) {
    showToast('error', `Could not change their access: ${message(error)}`);
    return false;
  }
}

/**
 * Put a chart in the Public catalog or take it out.
 *
 * The row is patched locally rather than re-fetched: `is_public` is the only
 * thing that changed, and the card's corner and the Public tab's count should
 * move the moment the switch does. The grants are re-read when the share
 * dialog is open on this chart, because "Everyone" is one of its rows.
 */
export async function setPublic(id: string, isPublic: boolean): Promise<boolean> {
  const chart = chartById(id);
  try {
    const answer = await setChartPublic(id, isPublic);
    catalogCharts.update((charts) =>
      charts.map((c) => (c.id === id ? { ...c, is_public: answer.is_public } : c)),
    );
    if (sharingChartId.value === id) chartGrants.set(await listChartGrants(id));
    const title = chart?.title ?? 'chart';
    showToast(
      'success',
      answer.is_public ? `"${title}" is now in the Public catalog` : `"${title}" is no longer public`,
    );
    return true;
  } catch (error) {
    showToast('error', `Could not change who can see it: ${message(error)}`);
    return false;
  }
}

export async function findPrincipals(query: string): Promise<PrincipalSearch> {
  try {
    return await searchPrincipals(query);
  } catch {
    // A failed lookup leaves the picker empty rather than erroring the dialog:
    // the person is still mid-typing.
    return { users: [], groups: [] };
  }
}
